import { memo, MouseEventHandler } from 'react';
import { BarStack as VisxBarStack, BarRounded } from '@visx/shape';
import { ScaleLinear } from 'd3-scale';
import { Text } from '@visx/text';

import { generateShortId } from '@/lib/utils/string';

import { CategoryData, SeriesMetaData } from '../grouped-chart/types';
import { generateStackedChartDataByKeys, mergeStackedSeries } from './utils';

import { CategoryScale, CategoryValue } from './types';

import commonStyles from '../charts.module.scss';

type BarStackProps = {
  data: CategoryData;
  keys: string[];
  valueScale: ScaleLinear<number, number>;
  categoryScale: CategoryScale;
  offset: number;
  barWidth: number;
  highlightKey?: string;
  onMouseMove?: MouseEventHandler<Element>;
  onMouseLeave?: MouseEventHandler<Element>;
};

interface StackedTimeSeriesData {
  minValue: number;
  maxValue: number;
  series: SeriesMetaData[];
  items: {
    categoryValue: CategoryValue;
    groupLabel?: string;
    stackTotal: number;
  }[];
}

const getStackedData = (data: CategoryData, keys: string[]): StackedTimeSeriesData =>
  generateStackedChartDataByKeys(data, keys);

export const BarStack = memo(function BarStack(props: BarStackProps) {
  const { data, keys } = props;
  const clipPathId = `clip-path-${generateShortId(6)}`;

  const mergedData = mergeStackedSeries(data, keys, 'total');
  const stackedData = getStackedData(data, keys);

  return (
    <>
      <defs>
        <clipPath id={clipPathId}>
          <BarStackInner
            {...props}
            data={getStackedData(mergedData, ['total'])}
            keys={['total']}
            rounded
          />
        </clipPath>
      </defs>

      <BarStackInner {...props} data={stackedData} clipPathId={clipPathId} />
      <BarStackInnerAxis {...props} data={stackedData} />
    </>
  );
});

type BarStackInnerProps = Omit<BarStackProps, 'data'> & {
  data: StackedTimeSeriesData;
  clipPathId?: string;
};

export const BarStackInner = (props: BarStackInnerProps & { rounded?: boolean }) => {
  const {
    data,
    keys,
    categoryScale,
    valueScale,
    clipPathId,
    barWidth,
    highlightKey,
    rounded = false,
  } = props;

  return (
    <VisxBarStack
      keys={keys}
      data={data.items}
      x={(d) => d.categoryValue.formatted}
      xScale={categoryScale}
      yScale={valueScale}
      color={(bar) => data.series.find(({ key }) => key === bar)?.color ?? ''}
      order="reverse">
      {(barStacks) =>
        barStacks.map((barStack, barStackIdx) =>
          barStack.bars.map((bar) => {
            if (bar.height === 0) {
              return null;
            }
            const barHeight = Math.max(1, bar.height); // very small clip paths misbehave in chrome, see SUP-3580
            const isHovered = highlightKey === barStack.key;
            const hasSomeSeriesHovered = highlightKey !== undefined;
            const fillOpacity = hasSomeSeriesHovered && !isHovered ? 0.3 : 0.9;

            return (
              <BarRounded
                key={`barstack-${barStack.index}-${bar.index}`}
                radius={rounded ? 5 : 0}
                x={
                  (categoryScale(bar.bar.data.categoryValue.formatted) ?? 0) +
                  categoryScale.bandwidth() / 2 -
                  barWidth / 2
                }
                y={bar.y}
                width={barWidth}
                height={barHeight}
                fill={bar.color}
                fillOpacity={fillOpacity}
                top={barStackIdx === barStacks.length - 1}
                clipPath={clipPathId !== undefined ? `url(#${clipPathId})` : undefined}
                onMouseMove={props.onMouseMove}
                onMouseLeave={props.onMouseLeave}
              />
            );
          }),
        )
      }
    </VisxBarStack>
  );
};

type BarStackInnerAxisProps = Omit<BarStackProps, 'data'> & {
  data: StackedTimeSeriesData;
};

export const BarStackInnerAxis = (props: BarStackInnerAxisProps) => {
  const { data, keys, categoryScale, valueScale } = props;

  return (
    <VisxBarStack
      keys={keys}
      data={data.items}
      x={(d) => d.categoryValue.formatted}
      xScale={categoryScale}
      yScale={valueScale}
      color={(bar) => data.series.find(({ key }) => key === bar)?.color ?? ''}>
      {(barStacks) =>
        barStacks.map((barStack) =>
          barStack.bars.map((bar) => (
            <Text
              key={`barstackaxis-${barStack.index}-${bar.index}`}
              x={categoryScale(bar.bar.data.categoryValue.formatted) ?? 0}
              y={valueScale(0) + 5}
              angle={-90}
              fontSize={12}
              className={commonStyles.tickLabel}
              textAnchor="end"
              verticalAnchor="middle"
              width={100}
              scaleToFit="shrink-only">
              Group
            </Text>
          )),
        )
      }
    </VisxBarStack>
  );
};
